import { memo, useMemo } from "react";
import type { IncidentFeature, IncidentCategory, IncidentStatus, FallbackTier } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  reports: IncidentFeature[];
  loading: boolean;
  onFlyTo?: (lng: number, lat: number) => void;
  onSelect?: (id: string) => void;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

const CAT_COLOR: Record<IncidentCategory, string> = {
  "traffic-accident":   "#F87171",
  "traffic-congestion": "#FB923C",
  "construction":       "#FBBF24",
  "flooding":           "#38BDF8",
  "waste":              "#A1A1AA",
  "lighting":           "#FDE047",
  "sidewalk":           "#A78BFA",
  "drainage":           "#7DD3FC",
  "trees":              "#34D399",
  "other":              "#6B7280",
};

const CAT_LABEL: Record<IncidentCategory, string> = {
  "traffic-accident":   "Accident",
  "traffic-congestion": "Congestion",
  "construction":       "Road/Construction",
  "flooding":           "Flooding",
  "waste":              "Waste",
  "lighting":           "Lighting",
  "sidewalk":           "Sidewalk",
  "drainage":           "Drainage",
  "trees":              "Trees",
  "other":              "Other",
};

const STATUS_ORDER: IncidentStatus[] = ["received", "assigned", "in-progress", "resolved"];
const STATUS_LABEL: Record<IncidentStatus, string> = {
  "received":    "RCVD",
  "assigned":    "ASGN",
  "in-progress": "PROG",
  "resolved":    "DONE",
};
const STATUS_COLOR: Record<IncidentStatus, string> = {
  "received":    "var(--text-3)",
  "assigned":    "var(--warn)",
  "in-progress": "var(--data)",
  "resolved":    "var(--good)",
};
const SEV_COLOR: Record<string, string> = {
  critical: "var(--bad)",
  high:     "var(--warn)",
  medium:   "var(--data)",
  low:      "var(--text-3)",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export const CityReportsDashboard = memo(function CityReportsDashboard({ reports, loading, onFlyTo, onSelect, ageMinutes, fallbackTier }: Props) {
  const stats = useMemo(() => {
    const byStatus = new Map<IncidentStatus, number>();
    const byCategory = new Map<IncidentCategory, number>();
    let open = 0;
    for (const r of reports) {
      byStatus.set(r.status, (byStatus.get(r.status) ?? 0) + 1);
      byCategory.set(r.category, (byCategory.get(r.category) ?? 0) + 1);
      if (r.status !== "resolved") open++;
    }
    const recent = [...reports]
      .sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime())
      .slice(0, 6);
    const sortedCats = [...byCategory.entries()]
      .sort((a, b) => b[1] - a[1])
      .filter(([, v]) => v > 0);
    const maxCat = Math.max(1, ...byCategory.values());
    return { byStatus, byCategory, open, recent, sortedCats, maxCat };
  }, [reports]);

  if (loading && reports.length === 0) {
    return (
      <div className="left-section cr-dash">
        <PanelHeader title="CITY REPORTS" ageMinutes={ageMinutes} fallbackTier={fallbackTier} source="traffy" />
        <span className="caption mono cr-loading">loading…</span>
      </div>
    );
  }

  return (
    <div className="left-section cr-dash">
      <PanelHeader
        title="CITY REPORTS"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="traffy"
        actions={
          <span className="caption mono" style={{ color: stats.open > 0 ? "var(--warn)" : "var(--good)" }}>
            {stats.open > 0 ? `${stats.open} OPEN` : "ALL CLEAR"} · {reports.length} TOTAL
          </span>
        }
      />

      {/* Status pipeline */}
      <div className="cr-pipeline">
        {STATUS_ORDER.map((s, i) => (
          <div key={s} className="cr-pipeline-step">
            {i > 0 && <span className="cr-pipeline-arrow">›</span>}
            <div className="cr-pipeline-inner">
              <span className="cr-pipeline-count mono" style={{ color: STATUS_COLOR[s] }}>
                {stats.byStatus.get(s) ?? 0}
              </span>
              <span className="cr-pipeline-label mono">{STATUS_LABEL[s]}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      {stats.sortedCats.length > 0 && (
        <div className="cr-categories">
          {stats.sortedCats.map(([cat, count]) => (
            <div key={cat} className="cr-cat-row" title={`${CAT_LABEL[cat]}: ${count} report${count !== 1 ? "s" : ""}`}>
              <span className="cr-cat-dot" style={{ background: CAT_COLOR[cat] }} />
              <span className="cr-cat-name mono">{CAT_LABEL[cat]}</span>
              <span className="cr-cat-bar">
                <span
                  className="cr-cat-fill"
                  style={{ width: `${(count / stats.maxCat) * 100}%`, background: CAT_COLOR[cat] }}
                />
              </span>
              <span className="cr-cat-count mono">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent reports */}
      {stats.recent.length > 0 && (
        <>
          <div className="cr-recent-head">
            <span className="caption mono" style={{ color: "var(--text-3)", letterSpacing: "0.08em" }}>RECENT</span>
          </div>
          <ul className="cr-recent">
            {stats.recent.map((r) => (
              <li
                key={r.id}
                className="cr-recent-item"
                onClick={() => {
                  onFlyTo?.(r.lng, r.lat);
                  onSelect?.(r.id);
                }}
                title={r.aiSummary ?? r.description ?? r.title}
              >
                <span className="cr-recent-sev" style={{ background: SEV_COLOR[r.severity] ?? "var(--text-3)" }} />
                <span className="cr-recent-body">
                  <span className="cr-recent-title">{r.title}</span>
                  {r.staffName && (
                    <span className="cr-recent-owner mono">{r.staffName}{r.teamName ? ` · ${r.teamName}` : ""}</span>
                  )}
                </span>
                <span className="cr-recent-right">
                  <span className="cr-recent-status mono" style={{ color: STATUS_COLOR[r.status] }}>
                    {STATUS_LABEL[r.status]}
                  </span>
                  <span className="cr-recent-age mono">{timeAgo(r.reportedAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {reports.length === 0 && (
        <div className="cr-empty caption mono">No reports yet · submit via LINE / Telegram</div>
      )}

      {/* Platform attribution */}
      <div className="cr-source mono">
        <span className="cr-source-dot" style={{ background: "#06C755" }} />LINE
        <span className="cr-source-dot" style={{ background: "#26A5E4" }} />Telegram
        <span className="cr-source-dot" style={{ background: "#A78BFA" }} />Traffy
      </div>
    </div>
  );
});
